import { ComponentRef, Injectable, Injector } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
  Router,
} from '@angular/router';

/**
 * The menu list and dish edit are sibling routes, so Angular destroys the list
 * on every edit. Keep the loaded list while the next URL is still a menu route
 * and put that same screen back on X / save.
 */
@Injectable()
export class MenuRouteReuseStrategy implements RouteReuseStrategy {
  private stored: DetachedRouteHandle | null = null;
  private storedRestaurantId: string | null = null;

  constructor(private injector: Injector) {}

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const url = this.navigationUrl();
    return !!url && this.isMenuList(route) && this.isMenuUrl(url);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!this.isMenuList(route)) return;
    // After a list is shown again, Angular passes null. The screen is already reattached.
    if (!handle) {
      this.stored = null;
      return;
    }
    if (this.stored && this.stored !== handle) this.discardStored();
    this.stored = handle;
    this.storedRestaurantId = localStorage.getItem('restaurant_id');
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.stored || !this.isMenuList(route)) return false;
    if (this.storedRestaurantId !== localStorage.getItem('restaurant_id')) {
      this.discardStored();
      return false;
    }
    return true;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.isMenuList(route)) return null;
    const handle = this.stored;
    this.stored = null;
    return handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private isMenuList(route: ActivatedRouteSnapshot): boolean {
    const path = route.routeConfig?.path;
    const parent = route.parent?.routeConfig?.path;
    return (
      route.outlet === 'primary' &&
      (parent === 'menu' || parent === 'menudishes') &&
      (path === '' || path === 'menu')
    );
  }

  private isMenuUrl(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return (
      path === '/menu' ||
      path.startsWith('/menu/') ||
      path === '/menudishes' ||
      path.startsWith('/menudishes/')
    );
  }

  /**
   * Router is created with this strategy, so resolve it only during navigation.
   * Only the in-flight URL counts. The current URL is still the list we are leaving.
   */
  private navigationUrl(): string | null {
    const router = this.injector.get(Router);
    const nav = router.getCurrentNavigation();
    const tree = nav?.finalUrl || nav?.extractedUrl;
    return tree ? tree.toString() : null;
  }

  private discardStored(): void {
    const handle = this.stored as { componentRef?: ComponentRef<unknown> } | null;
    this.stored = null;
    this.storedRestaurantId = null;
    const ref = handle?.componentRef;
    if (ref && typeof ref.destroy === 'function') ref.destroy();
  }
}
